import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RiskMetricsPanel = () => {
  const [selectedMetric, setSelectedMetric] = useState('var');

  const riskMetrics = [
    {
      id: 'var',
      name: 'Value at Risk (VaR)',
      value: '₹2.45L',
      change: '-8.2%',
      changeType: 'positive',
      description: '95% confidence, 1-day horizon',
      icon: 'TrendingDown',
      details: {
        '1Day': '₹2.45L',
        '1Week': '₹5.82L',
        '1Month': '₹12.34L'
      }
    },
    {
      id: 'cvar',
      name: 'Conditional VaR',
      value: '₹3.78L',
      change: '-12.5%',
      changeType: 'positive',
      description: 'Expected loss beyond VaR',
      icon: 'AlertTriangle',
      details: {
        '1Day': '₹3.78L',
        '1Week': '₹8.92L',
        '1Month': '₹18.45L'
      }
    },
    {
      id: 'beta',
      name: 'Portfolio Beta',
      value: '1.24',
      change: '+0.08',
      changeType: 'negative',
      description: 'Relative to NIFTY 50',
      icon: 'Activity',
      details: {
        'NIFTY50': '1.24',
        'BANKNIFTY': '1.18',
        'SENSEX': '1.22'
      }
    },
    {
      id: 'volatility',
      name: 'Annualized Volatility',
      value: '18.5%',
      change: '+2.1%',
      changeType: 'negative',
      description: 'Rolling 30-day average',
      icon: 'BarChart3',
      details: {
        '7Day': '16.2%',
        '30Day': '18.5%',
        '90Day': '19.8%'
      }
    }
  ];

  const correlationData = [
    { asset: 'NIFTY 50', correlation: 0.85, strength: 'Strong Positive' },
    { asset: 'BANK NIFTY', correlation: 0.72, strength: 'Strong Positive' },
    { asset: 'Bitcoin', correlation: 0.23, strength: 'Weak Positive' },
    { asset: 'Gold', correlation: -0.15, strength: 'Weak Negative' },
    { asset: 'USD/INR', correlation: -0.42, strength: 'Moderate Negative' }
  ];

  const stressTestScenarios = [
    {
      scenario: 'Market Crash (-20%)',
      impact: '-₹18.5L',
      probability: '2.5%',
      severity: 'high'
    },
    {
      scenario: 'Banking Crisis',
      impact: '-₹12.3L',
      probability: '5.0%',
      severity: 'medium'
    },
    {
      scenario: 'Crypto Collapse (-50%)',
      impact: '-₹8.7L',
      probability: '8.0%',
      severity: 'medium'
    },
    {
      scenario: 'Interest Rate Hike',
      impact: '-₹6.2L',
      probability: '15.0%',
      severity: 'low'
    }
  ];

  const selectedRiskMetric = riskMetrics?.find(m => m?.id === selectedMetric);

  const getCorrelationColor = (correlation) => {
    const abs = Math.abs(correlation);
    if (abs >= 0.7) return 'text-error';
    if (abs >= 0.4) return 'text-warning';
    return 'text-success';
  };

  const getCorrelationBar = (correlation) => {
    const width = Math.abs(correlation) * 100;
    const color = correlation >= 0 ? 'bg-primary' : 'bg-error';
    return (
      <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} rounded-full`}
          style={{ width: `${width}%` }}
        ></div>
      </div>
    );
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      {/* Risk Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {riskMetrics?.map((metric) => (
          <div 
            key={metric?.id}
            onClick={() => setSelectedMetric(metric?.id)}
            className={`bg-card border rounded-lg p-4 cursor-pointer transition-smooth hover:shadow-md ${
              selectedMetric === metric?.id ? 'border-primary bg-primary/5' : 'border-border'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name={metric?.icon} size={16} className="text-primary" />
              </div>
              <div className={`text-xs font-medium ${
                metric?.changeType === 'positive' ? 'text-success' : 'text-error'
              }`}>
                {metric?.change}
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-lg font-bold text-foreground">{metric?.value}</div>
              <div className="text-xs text-muted-foreground">{metric?.name}</div>
              <div className="text-xs text-muted-foreground">{metric?.description}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Detailed Risk Metric */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name={selectedRiskMetric?.icon} size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">{selectedRiskMetric?.name}</h3>
                <p className="text-sm text-muted-foreground">{selectedRiskMetric?.description}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="text-3xl font-bold text-foreground">{selectedRiskMetric?.value}</div>
            
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(selectedRiskMetric?.details)?.map(([period, value]) => (
                <div key={period} className="text-center p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm font-medium text-foreground">{value}</div>
                  <div className="text-xs text-muted-foreground">{period}</div>
                </div>
              ))}
            </div>

            <div className="pt-4 border-t border-border">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Risk Level</span>
                <div className="flex items-center space-x-2">
                  <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                    <div className="w-3/5 h-full bg-warning rounded-full"></div>
                  </div>
                  <span className="text-warning font-medium">Moderate</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Correlation Matrix */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="GitBranch" size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Asset Correlations</h3>
                <p className="text-sm text-muted-foreground">Portfolio correlation with major indices</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {correlationData?.map((item, index) => (
              <div key={index} className="flex items-center justify-between py-2">
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground">{item?.asset}</div>
                  <div className="text-xs text-muted-foreground">{item?.strength}</div>
                </div>
                <div className="flex items-center space-x-3">
                  {getCorrelationBar(item?.correlation)}
                  <div className={`text-sm font-medium w-12 text-right ${getCorrelationColor(item?.correlation)}`}>
                    {item?.correlation?.toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Stress Test Scenarios */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Zap" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Stress Test Scenarios</h3>
              <p className="text-sm text-muted-foreground">Potential portfolio impact under adverse conditions</p>
            </div>
          </div>
          <Button variant="outline" size="sm" iconName="RefreshCw" iconSize={14}>
            Run Tests
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stressTestScenarios?.map((scenario, index) => (
            <div key={index} className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-smooth">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-foreground">{scenario?.scenario}</h4>
                <div className={`text-xs px-2 py-1 rounded-full ${
                  scenario?.severity === 'high' ? 'bg-error/10 text-error' :
                  scenario?.severity === 'medium'? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
                }`}>
                  {scenario?.severity?.toUpperCase()}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className={`text-lg font-bold ${getSeverityColor(scenario?.severity)}`}>
                    {scenario?.impact}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Probability: {scenario?.probability}
                  </div>
                </div>
                <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                  <Icon name="AlertTriangle" size={16} className={getSeverityColor(scenario?.severity)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RiskMetricsPanel;